import { SupervisedUserCircle, Workspaces } from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupsIcon from '@mui/icons-material/Groups';
import HomeIcon from '@mui/icons-material/Home';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import NumbersIcon from '@mui/icons-material/Numbers';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import PersonIcon from '@mui/icons-material/Person';
import ReportIcon from '@mui/icons-material/Report';
import SummarizeIcon from '@mui/icons-material/Summarize';
import WalletIcon from '@mui/icons-material/Wallet';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { usePermissions } from '../hooks/use-permissions';
import { useSessionActions } from '../hooks/use-session-actions';
import { AgtStaffPermissions, AWS_GROUP } from '../types';
import { AllianceAppBarSpacer } from './alliance/alliance-app-bar';
import { AppBanners, MaintenanceBanner } from './alliance/app-banners';
import {
  AppNavDrawer,
  SIDEBAR_COLORS,
  SIDEBAR_WIDTH,
} from './alliance/app-nav-drawer';
import { CURRENT_APP } from './alliance/current-app';
import { AppShellBar } from './app-shell-bar';
import { RouteErrorBoundary } from './route-container/route-error-boundary';
import { SelectAgentImpersonate } from './select-agent-impersonate';

interface NavItem {
  name: string;
  icon: JSX.Element;
  path?: string;
  // External link, opened in a new tab
  href?: string;
  children?: NavItem[];
  // Only this Group can see this link
  hasGroup?: AWS_GROUP;
  // User must have one of the permissions
  permissions?: AWS_GROUP[];
  // Required permissions for agent staff users
  staffPermission?: AgtStaffPermissions;
}

const navLinks: NavItem[] = [
  {
    name: 'Home',
    icon: <HomeIcon color="inherit" />,
    path: '/home',
  },
  {
    name: 'Admin',
    icon: <AdminPanelSettingsIcon />,
    permissions: [
      AWS_GROUP.ReadAll,
      AWS_GROUP.Contracting,
      AWS_GROUP.AgentDataAdmin,
      AWS_GROUP.Staff_ReadOnly,
      AWS_GROUP.PermissionsAdmin,
    ],
    children: [
      {
        name: 'Approvals',
        icon: <PendingActionsIcon />,
        path: '/admin/approvals',
        permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Contracting],
      },
      {
        name: 'Agent',
        icon: <ManageAccountsIcon />,
        path: '/admin/agent',
        permissions: [
          AWS_GROUP.ReadAll,
          AWS_GROUP.Contracting,
          AWS_GROUP.AgentDataAdmin,
          AWS_GROUP.Staff_ReadOnly,
        ],
      },
      {
        name: 'CMS',
        icon: <FormatColorTextIcon />,
        path: '/admin/cms',
        permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Contracting],
      },
      {
        name: 'Staff',
        icon: <SupervisedUserCircle />,
        path: '/admin/staff',
        permissions: [
          AWS_GROUP.ReadAll,
          AWS_GROUP.PermissionsAdmin,
          AWS_GROUP.AgentDataAdmin,
        ],
      },
    ],
  },
  {
    name: 'Team Management',
    icon: <GroupsIcon />,
    path: '/team',
  },
  {
    name: 'Agent Profile',
    icon: <PersonIcon />,
    path: '/account',
    staffPermission: AgtStaffPermissions['Profile'],
  },
  {
    name: 'My Wallet',
    icon: <WalletIcon />,
    path: '/wallet',
    staffPermission: AgtStaffPermissions['Wallet'],
  },
  {
    name: 'Licenses',
    icon: <GavelIcon />,
    path: '/licenses',
  },
  {
    name: 'E&O',
    icon: <ReportIcon />,
    path: '/errors-omissions',
  },
  {
    name: 'Documents',
    icon: <PermMediaIcon />,
    path: '/documents',
  },
  {
    name: 'Contracting Requests',
    icon: <NewReleasesIcon />,
    path: '/requests',
  },
  {
    name: 'My Contracts',
    icon: <SummarizeIcon />,
    path: '/contracts',
  },
  {
    name: "My Carrier Writing #'s",
    icon: <NumbersIcon />,
    path: '/writing-numbers',
  },
  {
    name: 'My Appointments',
    icon: <AssignmentIcon />,
    path: '/appointments',
  },
  {
    name: 'My Staff',
    icon: <Workspaces />,
    path: '/my-staff',
  },
  // {
  //   name: 'Help and Support',
  //   icon: <ContactSupportIcon />,
  //   path: '/help',
  // },
];

// Kept from the original sidebar (the launcher also links to ARC)
const backToArc: NavItem = {
  name: 'Back to ARC',
  icon: <ArrowBackIcon />,
  href: 'https://arc.naaleads.com/',
};

// Sidebar nav colors (design: active row = accent at 12%, icon in the
// darker menu accent)
const NAV_COLORS = {
  text: '#1b1c1d',
  icon: '#00000099',
  activeIcon: '#00874a',
  activeBg: `${CURRENT_APP.accent}1f`,
  hover: '#0000000a',
};

const SIDEBAR_LINKS = [
  {
    label: 'Terms of service',
    href: 'https://a7d9021b860b18c4222a-d761a6fa6590560fc9f9e4d318d2556e.ssl.cf1.rackcdn.com/contracting/documents/alliance-terms-of-service.pdf',
  },
  {
    label: 'Privacy policy',
    href: 'https://www.integrity.com/privacy-policy',
  },
];

/**
 * Check a nav item against the user's groups, and for agent staff, against
 * the pages the agent has given them (My Staff is never shown to staff).
 */
function useCanView() {
  // Context
  const {
    state: { session },
  } = React.useContext(AuthContext);
  // Hooks
  const { checkPermissions, hasGroup } = usePermissions();

  // Check if user is an agent staff account
  const accessToken = session?.getAccessToken().decodePayload();
  const staffAgtNo = accessToken?.['myStaffForAgt'];
  // Check which pages are permitted for agent staff
  const staffPerms: string[] = accessToken?.['cognito:groups'] || [];

  return (item: NavItem) => {
    if (staffAgtNo && item.staffPermission) {
      if (!staffPerms.includes(item.staffPermission)) {
        return false;
      }
    } else if (staffAgtNo && item.path === '/my-staff') {
      return false;
    }

    if (item.hasGroup) {
      // User must have this specific group to view link
      return hasGroup(item.hasGroup);
    } else if (item.permissions) {
      // User must have one of the permissions
      return checkPermissions(item.permissions);
    }
    return true;
  };
}

/** The current page is this path or one of its sub-pages */
const isPathActive = (params: { pathname: string; path?: string }) =>
  Boolean(
    params.path &&
      (params.pathname === params.path ||
        params.pathname.startsWith(`${params.path}/`)),
  );

/** One 44px sidebar row: a route link, an external link, or a group toggle */
function NavRow(props: {
  item: NavItem;
  isActive: boolean;
  nested?: boolean;
  onClick?: () => void;
  trailing?: React.ReactNode;
  expanded?: boolean;
}) {
  // Props
  const { item, isActive } = props;

  let linkProps: object = {
    onClick: props.onClick,
    'aria-expanded': props.expanded,
  };
  if (item.path) {
    linkProps = { component: Link, to: item.path };
  } else if (item.href) {
    linkProps = {
      component: 'a',
      href: item.href,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  return (
    <ButtonBase
      {...linkProps}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        fontFamily: 'inherit',
        height: props.nested ? 40 : 44,
        width: '100%',
        justifyContent: 'flex-start',
        gap: 2,
        px: '12px',
        pl: props.nested ? '24px' : '12px',
        borderRadius: '10px',
        backgroundColor: isActive ? NAV_COLORS.activeBg : 'transparent',
        color: NAV_COLORS.text,
        fontSize: props.nested ? 14 : 15,
        fontWeight: isActive ? 500 : 400,
        textAlign: 'left',
        flex: 'none',
        '&:hover': {
          backgroundColor: isActive ? NAV_COLORS.activeBg : NAV_COLORS.hover,
        },
        '& .MuiSvgIcon-root': {
          fontSize: 20,
          color: isActive ? NAV_COLORS.activeIcon : NAV_COLORS.icon,
        },
      }}
    >
      {item.icon}
      <Box component="span" sx={{ flex: 1 }}>
        {item.name}
      </Box>
      {props.trailing}
    </ButtonBase>
  );
}

/** A sidebar entry: a link, or a collapsible group (Admin) */
function NavListItem(props: { item: NavItem }) {
  // Props
  const { item } = props;
  // Hooks
  const location = useLocation();
  const canView = useCanView();

  const childActive = Boolean(
    item.children?.some((child) =>
      isPathActive({ pathname: location.pathname, path: child.path }),
    ),
  );
  // State
  const [open, setOpen] = React.useState(childActive);

  React.useEffect(() => {
    // Open the group when navigating to one of its pages
    if (childActive) {
      setOpen(true);
    }
  }, [childActive]);

  if (!canView(item)) {
    return null;
  }

  if (item.children && item.children.length) {
    return (
      <React.Fragment>
        <NavRow
          item={item}
          isActive={false}
          expanded={open}
          onClick={() => setOpen((current) => !current)}
          trailing={
            open ? (
              <ExpandLess sx={{ fontSize: '20px !important' }} />
            ) : (
              <ExpandMore sx={{ fontSize: '20px !important' }} />
            )
          }
        />
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {item.children.filter(canView).map((child) => (
              <NavRow
                key={child.name}
                item={child}
                nested
                isActive={isPathActive({
                  pathname: location.pathname,
                  path: child.path,
                })}
              />
            ))}
          </Box>
        </Collapse>
      </React.Fragment>
    );
  }

  return (
    <NavRow
      item={item}
      isActive={isPathActive({ pathname: location.pathname, path: item.path })}
    />
  );
}

/**
 * The signed-in shell: one App Bar for every page, the sidebar (permanent at
 * md+, a temporary drawer below), the banner slot and the page outlet.
 *
 * @param maintenanceEnd - Set while maintenance is active for an admin; shows
 *   the admins-only maintenance banner
 */
export function Dashboard(props: { maintenanceEnd?: string | null }) {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  // Hooks
  const { signOut } = useSessionActions();

  return (
    <Box sx={{ display: 'flex' }}>
      <AppNavDrawer
        open={state.drawerOpen}
        onClose={() => dispatch({ type: 'DRAWER_CLOSE' })}
      >
        <Box
          sx={{
            p: '6px',
            backgroundColor: SIDEBAR_COLORS.strip,
            borderBottom: '1px solid #0000000f',
            position: 'relative',
            zIndex: 5,
          }}
        >
          <SelectAgentImpersonate />
        </Box>

        <Box
          sx={{
            minHeight: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <Box
            component="nav"
            aria-label="Contracting"
            sx={{
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              flex: 1,
            }}
          >
            {navLinks.map((navItem) => (
              <NavListItem key={navItem.name} item={navItem} />
            ))}
            <NavRow item={backToArc} isActive={false} />
          </Box>

          <Box
            sx={{
              p: '16px 20px',
              // Keep the links above the CookieYes consent button that is
              // fixed to the bottom-left corner
              pb: '72px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '6px',
            }}
          >
            {SIDEBAR_LINKS.map((link) => (
              <Box
                key={link.label}
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  color: '#00000080',
                  transition: 'color 0.2s',
                  '&:hover': { color: NAV_COLORS.activeIcon },
                }}
              >
                {link.label}
              </Box>
            ))}
          </Box>
        </Box>
      </AppNavDrawer>

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
        }}
      >
        <AppShellBar sidebar />

        <Box component="main">
          <AllianceAppBarSpacer />
          <AppBanners />
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </Box>
      </Box>

      {props.maintenanceEnd !== undefined && props.maintenanceEnd !== null ? (
        <MaintenanceBanner until={props.maintenanceEnd} onSignOut={signOut} />
      ) : null}
    </Box>
  );
}
